import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { usePartnerProgram } from "@/hooks/usePartnerProgram";
import { useToast } from "@/hooks/use-toast";

interface PartnerLogoutModalProps {
  open: boolean;
  onClose: () => void;
  onConfirmLogout: () => void;
}

const PartnerLogoutModal = ({ open, onClose, onConfirmLogout }: PartnerLogoutModalProps) => {
  const { submitFeedback } = usePartnerProgram();
  const { toast } = useToast();
  const [category, setCategory] = useState("idea");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmitAndLogout = async () => {
    if (!description.trim()) {
      onConfirmLogout();
      return;
    }

    setSubmitting(true);
    try {
      await submitFeedback({
        category,
        title: `Quick feedback (${category})`,
        description: description.trim(),
        priority: "nice_to_have",
        page_url: window.location.pathname,
      });
      toast({ title: "Thanks for your feedback!" });
    } catch (error) {
      console.error("Feedback error:", error);
    } finally {
      setSubmitting(false);
      onConfirmLogout();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Before you go — share a quick thought?</DialogTitle>
          <DialogDescription>
            As a development partner, your feedback shapes Arrowlink. Drop a quick note or skip for now.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger>
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="bug">🐛 Bug Report</SelectItem>
              <SelectItem value="idea">💡 Feature Idea</SelectItem>
              <SelectItem value="ux">🎨 UX Improvement</SelectItem>
              <SelectItem value="other">📝 Other</SelectItem>
            </SelectContent>
          </Select>

          <Textarea
            placeholder="What's on your mind? (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            maxLength={500}
          />
        </div>

        <DialogFooter className="flex gap-2 sm:gap-2">
          <Button variant="ghost" onClick={onConfirmLogout} disabled={submitting}>
            Skip & Sign Out
          </Button>
          <Button onClick={handleSubmitAndLogout} disabled={submitting}>
            {submitting ? "Sending..." : description.trim() ? "Submit & Sign Out" : "Sign Out"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PartnerLogoutModal;
